import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Link,
  Stack,
  Tabs,
  Tab,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'
import { Recruiter, RecruiterOutcome, OutreachType, recruitersApi, clientsApi, DreamJob } from '../../services/api'
import OutreachComposer from '../networking/OutreachComposer'
import DialogShell from '../common/DialogShell'
import PanelSection from '../common/PanelSection'
import { todayLocalYMD } from '../../utils/dateFormat'
import {
  sortRecruiterOutcomes,
  latestRecruiterOutcome,
  outcomeDateToIso,
  isoToLocalYmd,
} from '../../utils/recruiterOutcomes'
import { recipientFromRecruiter } from '../../utils/outreachRecipient'
import { searchPartnerMergeValues } from '../../utils/searchPartnerMergeValues'
import { useNotification } from '../../hooks/useNotification'
import { ensureHttpProtocol } from '../../utils/validation'
import { getEnhancedTableStyles } from '../../utils/tableStyles'
import { getCompactTableSx } from '../../utils/compactTableStyles'
import { ExpanderField, ExpanderHeaderCell, ExpanderRow, ExpanderToggleCell } from '../common/table/RowExpander'
import SortableTableHeader, { SortOrder } from '../common/table/SortableTableHeader'
import FilterBar, { FilterBarFilter } from '../common/table/FilterBar'
import { useTablePagination } from '../common/table/useTablePagination'
import TablePaginationBar from '../common/table/TablePaginationBar'

interface ExecutiveSearchTabProps {
  clientId?: string
  // MWA-357 — which list this is. Wild Card Outreach is a functional copy of
  // Search Firm on the same record, so one component renders both.
  outreachType?: OutreachType
  canEdit?: boolean
  // MWA-260 — portal clients may toggle the contacted flag without full edit
  // (add/delete) access. The backend (manage-recruiters) already authorizes a
  // client to update the contacted status on their own recruiter.
  canMarkContacted?: boolean
  // MWA-356 — the client sends the intro and receives the reply, so the portal
  // lets them record what the partner said. Editing or removing an outcome
  // stays with canEdit (staff); manage-recruiters refuses it for a client.
  canLogOutcome?: boolean
  // Show the in-tab "Add Recruiter" buttons (header + empty state). Defaults to
  // true; the staff Job Search page sets this false and adds via its header
  // "Add" menu instead — see autoOpenAdd below.
  showAddButton?: boolean
  // One-shot trigger from a parent "Add" menu: open the Add dialog and call
  // onAutoAddHandled so the parent can clear it (won't re-fire on remount).
  autoOpenAdd?: boolean
  onAutoAddHandled?: () => void
}

interface RecruiterFormState {
  name: string
  firm: string
  field: string
  email: string
  linkedinUrl: string
  emailTemplate: string
}

const EMPTY_FORM: RecruiterFormState = {
  name: '',
  firm: '',
  field: '',
  email: '',
  linkedinUrl: '',
  emailTemplate: '',
}

// MWA-357 — everything that differs between the two tabs. Behaviour is
// identical; only the words and the intro template category change.
const OUTREACH_COPY: Record<
  OutreachType,
  {
    title: string
    subtitle: string
    addLabel: string
    nameLabel: string
    firmLabel: string
    emptyTitle: string
    emptyEditable: string
    emptyReadOnly: string
    noMatches: string
    loadError: string
    added: string
    addFailed: string
    removed: string
    removeFailed: string
    overline: string
    closeLabel: string
    noTemplate: string
    deleteLabel: string
    thisEntry: string
    templateHelper: string
    templateCategory: string
  }
> = {
  search_firm: {
    title: 'Executive Search Recruiters',
    subtitle: 'Recruiters and search firms to reach out to, with a ready-to-send outreach template.',
    addLabel: 'Add Recruiter',
    nameLabel: 'Recruiter Name',
    firmLabel: 'Firm',
    emptyTitle: 'No recruiters yet',
    emptyEditable: 'Add executive search recruiters to start tracking your outreach.',
    emptyReadOnly: 'Executive search recruiters will appear here once added.',
    noMatches: 'No recruiters match the current filters.',
    loadError: 'Failed to load recruiters',
    added: 'Recruiter added',
    addFailed: 'Failed to add recruiter',
    removed: 'Recruiter removed',
    removeFailed: 'Failed to remove recruiter',
    overline: 'search partners',
    closeLabel: 'Close recruiter details',
    noTemplate: 'No template provided for this recruiter.',
    deleteLabel: 'Delete Recruiter',
    thisEntry: 'this recruiter',
    templateHelper: "The email you'll copy and send to this recruiter.",
    templateCategory: 'search_partner',
  },
  // Luke, Sept 17: companies outside the client's network — mostly startups
  // looking to add executives from big companies — approached cold.
  wildcard: {
    title: 'Wild Card Outreach',
    subtitle: "Companies outside the client's network to approach cold, with a ready-to-send outreach template.",
    addLabel: 'Add Wild Card Entry',
    nameLabel: 'Contact Name',
    firmLabel: 'Company',
    emptyTitle: 'No wild card targets yet',
    emptyEditable: "Add companies outside the client's network to start tracking wild card outreach.",
    emptyReadOnly: 'Wild card targets will appear here once your strategist adds them.',
    noMatches: 'No entries match the current filters.',
    loadError: 'Failed to load wild card outreach',
    added: 'Wild card entry added',
    addFailed: 'Failed to add wild card entry',
    removed: 'Wild card entry removed',
    removeFailed: 'Failed to remove wild card entry',
    overline: 'wild card targets',
    closeLabel: 'Close wild card details',
    noTemplate: 'No template provided for this entry.',
    deleteLabel: 'Delete Entry',
    thisEntry: 'this entry',
    templateHelper: "The email you'll copy and send to this contact.",
    templateCategory: 'wildcard_outreach',
  },
}

function ContactedChip({ contacted }: { contacted: boolean }) {
  return contacted ? (
    <Chip
      icon={<CheckCircleIcon />}
      label="Contacted"
      color="success"
      size="small"
      sx={{ fontWeight: 600 }}
    />
  ) : (
    <Chip label="Not contacted" variant="outlined" size="small" color="default" />
  )
}

export default function ExecutiveSearchTab({
  clientId,
  outreachType = 'search_firm',
  canEdit = false,
  canMarkContacted = false,
  canLogOutcome = false,
  showAddButton = true,
  autoOpenAdd = false,
  onAutoAddHandled,
}: ExecutiveSearchTabProps) {
  const [recruiters, setRecruiters] = useState<Recruiter[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const copy = OUTREACH_COPY[outreachType]
  const canToggleContact = canEdit || canMarkContacted
  const canAddOutcome = canEdit || canLogOutcome

  // Detail modal state
  const [selected, setSelected] = useState<Recruiter | null>(null)
  const [view, setView] = useState<'record' | 'outreach'>('record')
  const [copied, setCopied] = useState(false)
  const [savingContacted, setSavingContacted] = useState(false)
  const [savingFollowUp, setSavingFollowUp] = useState(false)

  // MWA-356 — outcome log
  const [outcomeText, setOutcomeText] = useState('')
  const [outcomeDate, setOutcomeDate] = useState(todayLocalYMD())
  const [savingOutcome, setSavingOutcome] = useState(false)
  const [outcomeError, setOutcomeError] = useState<string | null>(null)
  const [editingOutcomeId, setEditingOutcomeId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [editDate, setEditDate] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  const [confirmDelete, setConfirmDelete] = useState<
    { kind: 'recruiter' } | { kind: 'outcome'; outcome: RecruiterOutcome } | null
  >(null)

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<RecruiterFormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { showSuccess, showError } = useNotification()

  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  // 768-1199: Name, Firm, Contacted and Latest Outcome at fixed widths, with
  // Field / Specialty, Email and LinkedIn in a row expander, so the table fits
  // beside the 260px rail without scrolling sideways. Phones keep the full table.
  const isCompact = useMediaQuery(theme.breakpoints.between(768, 1200))
  const compactWidth = (width: number | string | Record<string, number | string>) =>
    isCompact ? { width } : undefined
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // CrowCase-style sort + filter
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<SortOrder>('asc')
  const [contactedFilter, setContactedFilter] = useState<string[]>([])

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  const recruiterFilterConfig: FilterBarFilter[] = [
    {
      name: 'contacted',
      label: 'Contacted',
      options: [
        { value: 'contacted', label: 'Contacted' },
        { value: 'not_contacted', label: 'Not contacted' },
      ],
      value: contactedFilter,
    },
  ]

  const displayRecruiters = useMemo(() => {
    const filtered = recruiters.filter((r) => {
      if (contactedFilter.length > 0) {
        const match = contactedFilter.some((v) =>
          v === 'contacted' ? r.contacted : !r.contacted
        )
        if (!match) return false
      }
      return true
    })
    if (!orderBy) return filtered
    return filtered.slice().sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (orderBy) {
        case 'name':
          aVal = a.name || ''
          bVal = b.name || ''
          break
        case 'firm':
          aVal = a.firm || ''
          bVal = b.firm || ''
          break
        case 'field':
          aVal = a.field || ''
          bVal = b.field || ''
          break
        case 'contacted':
          aVal = a.contacted ? 1 : 0
          bVal = b.contacted ? 1 : 0
          break
        default:
          return 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return order === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
  }, [recruiters, orderBy, order, contactedFilter])

  // MWA-254 Defect 2 — client-side pagination over the filtered/sorted rows.
  // Reset to page 1 whenever the sort or filter signature changes.
  const { paginated: pagedRecruiters, ...pageCtl } = useTablePagination(
    displayRecruiters,
    `${orderBy}|${order}|${contactedFilter.join(',')}`
  )

  // MWA-310 — the intro answers the recruiter's standard questions from what
  // the client already told us, so staff are not retyping it per partner.
  const [dreamJob, setDreamJob] = useState<DreamJob | undefined>(undefined)
  // The modal covers the page header that names the client, and staff move
  // between clients all day, so it restates whose partners these are — the
  // same reason the networking contact modal does.
  const [clientName, setClientName] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!clientId) {
      setDreamJob(undefined)
      setClientName(undefined)
      return
    }
    let cancelled = false
    clientsApi
      .get(clientId)
      .then((c) => {
        if (cancelled) return
        setDreamJob(c?.dreamJob)
        setClientName(c?.name)
      })
      .catch((err) => {
        // The intro still works; its answers just arrive blank, and the
        // composer already warns about an empty merge field before send.
        console.error('Failed to read the client for intro pre-fill:', err)
        if (!cancelled) {
          setDreamJob(undefined)
          setClientName(undefined)
        }
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  const loadRecruiters = useCallback(async () => {
    if (!clientId) {
      setRecruiters([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await recruitersApi.list({ clientId, outreachType })
      setRecruiters(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to load recruiters:', err)
      setError(copy.loadError)
    } finally {
      setLoading(false)
    }
    // copy follows outreachType, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, outreachType])

  useEffect(() => {
    loadRecruiters()
  }, [loadRecruiters])

  const resetOutcomeForms = () => {
    setOutcomeText('')
    setOutcomeDate(todayLocalYMD())
    setOutcomeError(null)
    setEditingOutcomeId(null)
  }

  const handleRowClick = (recruiter: Recruiter) => {
    if (recruiter.id !== selected?.id) resetOutcomeForms()
    setSelected(recruiter)
    setView('record')
    setCopied(false)
  }

  const handleCloseModal = () => {
    setSelected(null)
    setCopied(false)
  }

  // Refresh the open modal as well as the table: updating only the list
  // leaves `selected` holding the pre-write row, so the modal would still
  // show the old state until closed and reopened.
  const applyRow = (row: Recruiter) => {
    setSelected((current) => (current?.id === row.id ? row : current))
    setRecruiters((prev) => prev.map((r) => (r.id === row.id ? row : r)))
  }

  const errorMessage = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback

  const handleCopyTemplate = async () => {
    if (!selected?.emailTemplate) return
    try {
      await navigator.clipboard.writeText(selected.emailTemplate)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      showError('Failed to copy to clipboard')
    }
  }

  const handleToggleContacted = async () => {
    if (!selected) return
    const next = !selected.contacted
    setSavingContacted(true)
    try {
      applyRow(await recruitersApi.markContacted(selected.id, next))
      showSuccess(next ? 'Marked as contacted' : 'Marked as not contacted')
    } catch (err) {
      console.error('Failed to update contacted status:', err)
      showError('Failed to update contacted status')
    } finally {
      setSavingContacted(false)
    }
  }

  // MWA-356 — the second touch. Luke, [38:44]: partners are "meant to reach out
  // two times", so this records that the follow-up actually went out.
  const handleToggleFollowedUp = async () => {
    if (!selected) return
    const next = !selected.followedUpAt
    setSavingFollowUp(true)
    try {
      applyRow(await recruitersApi.markFollowedUp(selected.id, next))
      showSuccess(next ? 'Follow-up recorded' : 'Follow-up cleared')
    } catch (err) {
      console.error('Failed to update follow-up:', err)
      showError(errorMessage(err, 'Failed to update follow-up'))
    } finally {
      setSavingFollowUp(false)
    }
  }

  const handleAddOutcome = async () => {
    if (!selected) return
    const text = outcomeText.trim()
    if (!text) {
      setOutcomeError('Describe what the partner said.')
      return
    }
    setSavingOutcome(true)
    setOutcomeError(null)
    try {
      applyRow(await recruitersApi.addOutcome(selected.id, { text, occurredAt: outcomeDateToIso(outcomeDate) }))
      setOutcomeText('')
      setOutcomeDate(todayLocalYMD())
      showSuccess('Outcome logged')
    } catch (err) {
      // The typed text stays in the field: a refused write that cleared the
      // form would look exactly like a saved one.
      console.error('Failed to log outcome:', err)
      setOutcomeError(errorMessage(err, 'Failed to log outcome'))
    } finally {
      setSavingOutcome(false)
    }
  }

  const startEditOutcome = (outcome: RecruiterOutcome) => {
    setEditingOutcomeId(outcome.id)
    setEditText(outcome.text)
    setEditDate(isoToLocalYmd(outcome.occurredAt))
    setOutcomeError(null)
  }

  const handleSaveEdit = async () => {
    if (!selected || !editingOutcomeId) return
    const text = editText.trim()
    if (!text) {
      setOutcomeError('An outcome cannot be empty.')
      return
    }
    setSavingEdit(true)
    setOutcomeError(null)
    try {
      applyRow(
        await recruitersApi.updateOutcome(selected.id, editingOutcomeId, {
          text,
          occurredAt: outcomeDateToIso(editDate),
        })
      )
      setEditingOutcomeId(null)
      showSuccess('Outcome updated')
    } catch (err) {
      console.error('Failed to update outcome:', err)
      setOutcomeError(errorMessage(err, 'Failed to update outcome'))
    } finally {
      setSavingEdit(false)
    }
  }

  const handleOpenAdd = () => {
    setForm(EMPTY_FORM)
    setAddOpen(true)
  }

  // Open the Add dialog when a parent "Add" menu requests it, then acknowledge
  // so the trigger is one-shot (won't re-fire on remount).
  useEffect(() => {
    if (autoOpenAdd) {
      handleOpenAdd()
      onAutoAddHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd])

  const handleSubmitAdd = async () => {
    if (!clientId) {
      showError('No client selected')
      return
    }
    if (!form.name.trim()) {
      showError(`${copy.nameLabel} is required`)
      return
    }
    setSubmitting(true)
    try {
      const created = await recruitersApi.create({
        clientId,
        outreachType,
        name: form.name.trim(),
        firm: form.firm.trim(),
        field: form.field.trim(),
        email: form.email.trim(),
        linkedinUrl: form.linkedinUrl.trim(),
        emailTemplate: form.emailTemplate,
      })
      setRecruiters((prev) => [...prev, created])
      showSuccess(copy.added)
      setAddOpen(false)
      setForm(EMPTY_FORM)
    } catch (err) {
      console.error('Failed to add recruiter:', err)
      showError(copy.addFailed)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (recruiter: Recruiter) => {
    setConfirmDelete(null)
    setDeletingId(recruiter.id)
    try {
      await recruitersApi.delete(recruiter.id)
      setRecruiters((prev) => prev.filter((r) => r.id !== recruiter.id))
      if (selected?.id === recruiter.id) handleCloseModal()
      showSuccess(copy.removed)
    } catch (err) {
      console.error('Failed to remove recruiter:', err)
      showError(copy.removeFailed)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDeleteOutcome = async (outcome: RecruiterOutcome) => {
    if (!selected) return
    setConfirmDelete(null)
    setDeletingId(outcome.id)
    setOutcomeError(null)
    try {
      applyRow(await recruitersApi.deleteOutcome(selected.id, outcome.id))
      if (editingOutcomeId === outcome.id) setEditingOutcomeId(null)
      showSuccess('Outcome removed')
    } catch (err) {
      console.error('Failed to remove outcome:', err)
      setOutcomeError(errorMessage(err, 'Failed to remove outcome'))
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const handleCopyEmail = async (email: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(email)
      showSuccess('Email copied')
    } catch {
      showError('Failed to copy email')
    }
  }

  // The table cells and the tablet row expander both show these.
  const renderEmail = (recruiter: Recruiter) =>
    recruiter.email ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Link
          href={`mailto:${recruiter.email}`}
          onClick={(e) => e.stopPropagation()}
          variant="body2"
          sx={{ wordBreak: 'break-all' }}
        >
          {recruiter.email}
        </Link>
        <Tooltip title="Copy email">
          <IconButton size="small" onClick={(e) => handleCopyEmail(recruiter.email, e)}>
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      '—'
    )

  const renderLinkedIn = (url: string) => (
    <Link
      href={ensureHttpProtocol(url)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      variant="body2"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      Profile
      <OpenInNewIcon fontSize="inherit" />
    </Link>
  )

  // The Record view's working column and reference rail. The Outreach view is
  // the composer alone, so it takes the shell's ordinary body: the shell's
  // `split` is all-or-nothing.
  const outcomeEntrySx = {
    py: 1.25,
    borderTop: 1,
    borderColor: 'divider',
    '&:first-of-type': { borderTop: 0, pt: 0 },
    '&:last-of-type': { pb: 0 },
  } as const

  const recordMain = selected ? (
    <>
      <PanelSection first dense title="Status">
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ContactedChip contacted={selected.contacted} />
            {selected.contacted && selected.contactedAt && (
              <Typography variant="caption" color="text.secondary">
                {formatDate(selected.contactedAt)}
              </Typography>
            )}
            {/* MWA-310 — Dee: "they should reach out again... in a few
                months to stay top of [mind]". A date to read, not a
                reminder that fires; nothing reads this on a schedule. */}
            {selected.contacted && selected.reTouchAt && (
              <Typography variant="caption" color="text.secondary">
                · follow up by {formatDate(selected.reTouchAt)}
              </Typography>
            )}
          </Box>
          {selected.followedUpAt && (
            <Typography variant="body2">Followed up {formatDate(selected.followedUpAt)}</Typography>
          )}
          {canToggleContact && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Button
                variant={selected.contacted ? 'outlined' : 'contained'}
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleToggleContacted}
                disabled={savingContacted}
                sx={{ minHeight: { xs: 44, sm: 36 } }}
              >
                {selected.contacted ? 'Mark Not Contacted' : 'Mark Contacted'}
              </Button>
              <Tooltip title={selected.contacted ? '' : 'Mark contacted first'}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleToggleFollowedUp}
                    disabled={savingFollowUp || !selected.contacted}
                    sx={{ minHeight: { xs: 44, sm: 36 } }}
                  >
                    {selected.followedUpAt ? 'Clear Follow-up' : 'Mark Followed Up'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </PanelSection>

      <PanelSection
        dense
        title="Outreach Email Template"
        aside={
          <Button
            size="small"
            startIcon={<CopyIcon />}
            onClick={handleCopyTemplate}
            disabled={!selected.emailTemplate}
            sx={{ minHeight: { xs: 44, sm: 36 } }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        }
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderColor: 'divider',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            // Deliberately no maxHeight or overflow of its own: a
            // second scroll region inside the modal's one is the
            // defect this surface was known for.
          }}
        >
          <Typography variant="body2" color={selected.emailTemplate ? 'text.primary' : 'text.secondary'}>
            {selected.emailTemplate || copy.noTemplate}
          </Typography>
        </Paper>
      </PanelSection>

      {/* MWA-356 — Luke, [38:55]: when a partner replies "let's
          talk… I have a role", "we want them to be able to log an
          outcome." Free text and the date it happened. */}
      <PanelSection dense title="Outcomes">
        <Stack spacing={1.5}>
          {outcomeError && (
            <Alert severity="error" onClose={() => setOutcomeError(null)}>
              {outcomeError}
            </Alert>
          )}
          {canAddOutcome &&
            (selected.contacted ? (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    label="What did they say?"
                    placeholder="For example: wants a call next week about a VP role."
                    // Raised so the example text shows under it; an
                    // unraised label sits on top of the placeholder.
                    InputLabelProps={{ shrink: true }}
                    value={outcomeText}
                    onChange={(e) => setOutcomeText(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                    disabled={savingOutcome}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-end"
                    justifyContent="space-between"
                    useFlexGap
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {/* Defaults to today; capped at today, because an
                        outcome is a thing that happened. */}
                    <TextField
                      size="small"
                      type="date"
                      label="When"
                      value={outcomeDate}
                      onChange={(e) => setOutcomeDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: todayLocalYMD() }}
                      sx={{ minWidth: 150 }}
                      disabled={savingOutcome}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddOutcome}
                      disabled={savingOutcome || !outcomeText.trim() || !outcomeDate}
                      sx={{ minHeight: { xs: 44, sm: 36 } }}
                    >
                      {savingOutcome ? 'Logging...' : 'Log Outcome'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Mark {selected.name} contacted before logging what they said.
              </Typography>
            ))}

          {sortRecruiterOutcomes(selected.outcomes).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No outcomes yet
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {sortRecruiterOutcomes(selected.outcomes).map((o) => (
                <Box component="li" key={o.id} sx={outcomeEntrySx}>
                  {editingOutcomeId === o.id ? (
                    <Stack spacing={1} alignItems="flex-start">
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        label="Outcome"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        inputProps={{ maxLength: 2000 }}
                        disabled={savingEdit}
                      />
                      <Stack direction="row" spacing={1} alignItems="flex-end" useFlexGap sx={{ flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          type="date"
                          label="When"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ max: todayLocalYMD() }}
                          sx={{ minWidth: 150 }}
                          disabled={savingEdit}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSaveEdit}
                          disabled={savingEdit || !editText.trim() || !editDate}
                        >
                          {savingEdit ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="small" onClick={() => setEditingOutcomeId(null)} disabled={savingEdit}>
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatDate(o.occurredAt)}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {o.text}
                        </Typography>
                      </Box>
                      {canEdit && (
                        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <Tooltip title="Edit outcome">
                            <IconButton size="small" aria-label="Edit outcome" onClick={() => startEditOutcome(o)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove outcome">
                            <IconButton
                              size="small"
                              aria-label="Remove outcome"
                              onClick={() => setConfirmDelete({ kind: 'outcome', outcome: o })}
                              disabled={deletingId === o.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </PanelSection>
    </>
  ) : null

  const recordRail = selected ? (
    <>
      <PanelSection first dense title="Details">
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            {selected.email ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Link href={`mailto:${selected.email}`} variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selected.email}
                </Link>
                <Tooltip title="Copy email">
                  <IconButton
                    size="small"
                    aria-label="Copy email"
                    onClick={(e) => handleCopyEmail(selected.email, e)}
                  >
                    <CopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          {selected.linkedinUrl && (
            <Box>
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                href={ensureHttpProtocol(selected.linkedinUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn Profile
              </Button>
            </Box>
          )}
        </Stack>
      </PanelSection>

      {canEdit && (
        <Box sx={{ mt: 2 }}>
          <PanelSection dense>
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmDelete({ kind: 'recruiter' })}
              disabled={deletingId === selected.id}
            >
              {copy.deleteLabel}
            </Button>
          </PanelSection>
        </Box>
      )}
    </>
  ) : null

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6">{copy.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.subtitle}
          </Typography>
        </Box>
        {canEdit && showAddButton && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={!clientId}>
            {copy.addLabel}
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button onClick={loadRecruiters} sx={{ mt: 1 }} size="small">
            Retry
          </Button>
        </Box>
      )}

      {!loading && !error && recruiters.length === 0 && (
        <Paper variant="outlined" sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <BusinessIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">
            {copy.emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {canEdit
              ? copy.emptyEditable
              : copy.emptyReadOnly}
          </Typography>
          {canEdit && showAddButton && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={!clientId}>
              {copy.addLabel}
            </Button>
          )}
        </Paper>
      )}

      {!loading && !error && recruiters.length > 0 && (
        <>
          <FilterBar
            filters={recruiterFilterConfig}
            onChange={(name, value) => name === 'contacted' && setContactedFilter(value as string[])}
            onClearAll={() => setContactedFilter([])}
          />
          {displayRecruiters.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {copy.noMatches}
              </Typography>
            </Box>
          ) : (
            <Paper variant="outlined" sx={tableStyles.tableContainer}>
              <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                <Table stickyHeader sx={{ ...tableStyles.table, ...(isCompact ? getCompactTableSx(theme) : undefined) }}>
                  <TableHead>
                    <TableRow>
                      {/* The name has no width at tablet: it takes what the others leave. */}
                      <SortableTableHeader id="name" label={copy.nameLabel} orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableHeader id="firm" label={copy.firmLabel} orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth('24%')} />
                      {!isCompact && (
                        <>
                          <SortableTableHeader id="field" label="Field / Specialty" orderBy={orderBy} order={order} onSort={handleSort} />
                          <TableCell>Email</TableCell>
                          <TableCell>LinkedIn</TableCell>
                        </>
                      )}
                      <SortableTableHeader id="contacted" label="Contacted" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 124, md: 136 })} />
                      <TableCell sx={compactWidth({ xs: 104, md: 120 })}>Latest Outcome</TableCell>
                      {isCompact && <ExpanderHeaderCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRecruiters.map((recruiter) => (
                <Fragment key={recruiter.id}>
                <TableRow
                  hover
                  onClick={() => handleRowClick(recruiter)}
                  // Focusable so the modal hands focus back to the row it
                  // opened from, and so a keyboard user can open it at all.
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleRowClick(recruiter)
                    }
                  }}
                  aria-label={`Open ${recruiter.name}`}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {recruiter.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{recruiter.firm || '—'}</TableCell>
                  {!isCompact && (
                    <>
                      <TableCell>{recruiter.field || '—'}</TableCell>
                      <TableCell>{renderEmail(recruiter)}</TableCell>
                      <TableCell>
                        {/* Hide the cell content entirely when there's no LinkedIn URL */}
                        {recruiter.linkedinUrl ? renderLinkedIn(recruiter.linkedinUrl) : null}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <ContactedChip contacted={recruiter.contacted} />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const latest = latestRecruiterOutcome(recruiter.outcomes)
                      if (!latest) return '—'
                      const more = (recruiter.outcomes?.length ?? 0) - 1
                      return (
                        <Tooltip title={latest.text}>
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={`${formatDate(latest.occurredAt)}${more > 0 ? ` · +${more}` : ''}`}
                          />
                        </Tooltip>
                      )
                    })()}
                  </TableCell>
                  {isCompact && (
                    <ExpanderToggleCell
                      open={expandedId === recruiter.id}
                      onToggle={() => setExpandedId(expandedId === recruiter.id ? null : recruiter.id)}
                      subject={recruiter.name}
                    />
                  )}
                </TableRow>
                {isCompact && (
                  <ExpanderRow open={expandedId === recruiter.id} colSpan={5}>
                    <ExpanderField label="Field / Specialty">
                      <Typography variant="body2">{recruiter.field || '—'}</Typography>
                    </ExpanderField>
                    <ExpanderField label="Email">
                      <Typography component="div" variant="body2">
                        {renderEmail(recruiter)}
                      </Typography>
                    </ExpanderField>
                    <ExpanderField label="LinkedIn">
                      {recruiter.linkedinUrl ? (
                        renderLinkedIn(recruiter.linkedinUrl)
                      ) : (
                        <Typography variant="body2">—</Typography>
                      )}
                    </ExpanderField>
                  </ExpanderRow>
                )}
                </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <TablePaginationBar
                count={pageCtl.count}
                page={pageCtl.page}
                rowsPerPage={pageCtl.rowsPerPage}
                setPage={pageCtl.setPage}
                setRowsPerPage={pageCtl.setRowsPerPage}
              />
            </Paper>
          )}
        </>
      )}

      {/* Recruiter detail modal. MWA-356 — the same surface as the networking
          contact modal (ContactDetailPanel): a centred dialog on desktop, a
          full-screen sheet below 768px, a fixed identity header, and Record /
          Outreach views of one record rather than a side drawer. */}
      <DialogShell
        open={!!selected}
        onClose={handleCloseModal}
        maxWidth="md"
        fullScreenBelow={768}
        closeLabel={copy.closeLabel}
        // A client reading their own partners already knows whose they are;
        // staff move between clients all day.
        overline={canEdit && clientName ? <>{clientName}&rsquo;s {copy.overline}</> : undefined}
        title={selected?.name}
        subtitle={
          selected && (selected.firm || selected.field)
            ? [selected.firm, selected.field].filter(Boolean).join(' — ')
            : undefined
        }
        belowHeader={
          selected && (
            <Tabs
              value={view}
              onChange={(_, next) => setView(next as 'record' | 'outreach')}
              sx={{ px: { xs: 2, sm: 2.5 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
            >
              <Tab value="record" label="Record" />
              <Tab value="outreach" label="Outreach" />
            </Tabs>
          )
        }
        split={selected && view === 'record' ? { main: recordMain, rail: recordRail } : undefined}
      >
        {selected && view === 'outreach' && (
          /* MWA-310 — the standard intro. Dee: search partners are "super
             busy and the whole world wants to talk to them", so the intro
             answers their questions up front rather than being another
             "random outreach of 'Hi'". Same composer the networking side
             uses; only the recipient and what recording means differ. */
          <OutreachComposer
            recipient={recipientFromRecruiter(selected)}
            // A search partner is not a networking contact, and the
            // interaction record is keyed by contactId or interviewId.
            // Marking the partner contacted is the equivalent record,
            // and it already drives this tab's Contacted column.
            onRecordOutreach={async () => {
              const before = selected.contacted
              applyRow(await recruitersApi.markContacted(selected.id, true))
              return {
                undo: async () => {
                  applyRow(await recruitersApi.markContacted(selected.id, before))
                },
              }
            }}
            // A form letter to a recruiter is an email, never a text.
            allowSms={false}
            categoryFilter={(slug) => slug === copy.templateCategory}
            extraMergeValues={searchPartnerMergeValues(dreamJob)}
            clientTargetRole={dreamJob?.targetRole}
            onDone={() => setView('record')}
          />
        )}
      </DialogShell>

      <DialogShell
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        compact
        maxWidth="xs"
        title={confirmDelete?.kind === 'outcome' ? 'Remove Outcome' : copy.deleteLabel}
        footer={
          <>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDelete?.kind === 'outcome') handleDeleteOutcome(confirmDelete.outcome)
                else if (selected) handleDelete(selected)
              }}
            >
              {confirmDelete?.kind === 'outcome' ? 'Remove' : 'Delete'}
            </Button>
          </>
        }
      >
        <Typography>
          {confirmDelete?.kind === 'outcome'
            ? 'This removes the outcome from the record. It cannot be undone.'
            : `This will remove ${selected?.name ?? copy.thisEntry}. It cannot be undone.`}
        </Typography>
      </DialogShell>

      {/* Add recruiter dialog */}
      <DialogShell
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        title={copy.addLabel}
        footer={
          <>
            <Button onClick={() => setAddOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmitAdd} disabled={submitting || !form.name.trim()}>
              {submitting ? 'Adding...' : copy.addLabel}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={copy.nameLabel}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              required
            />
            <TextField
              label={copy.firmLabel}
              value={form.firm}
              onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Field / Specialty"
              value={form.field}
              onChange={(e) => setForm((f) => ({ ...f, field: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
          <TextField
            label="LinkedIn URL"
            value={form.linkedinUrl}
            onChange={(e) => setForm((f) => ({ ...f, linkedinUrl: e.target.value }))}
            size="small"
            fullWidth
            placeholder="https://www.linkedin.com/in/..."
          />
          <TextField
            label="Outreach Email Template"
            value={form.emailTemplate}
            onChange={(e) => setForm((f) => ({ ...f, emailTemplate: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={6}
            helperText={copy.templateHelper}
          />
        </Box>
      </DialogShell>
    </Box>
  )
}
